import * as React from "react";
import { Button, Input, notification } from "antd";
import { ChevronDown, ChevronUp } from "react-feather";
import { SyntaxHighlight } from "../../../../../components/syntaxHighlight/SyntaxHighlight";
import { ExternalLink } from "../../../../../newcomponents/table/components/ExternalLink";
import { CodeEditor } from "../../../../group/identityMatcher/codeEditor";
import { homeLink, request } from "../../../../../services/cpln";

interface Props {
  defaultSecretName?: string;
  onSecretCreated?: (secretName: string) => void;
}
export const ProviderGCPInstructions: React.FC<Props> = ({ defaultSecretName = "", onSecretCreated }) => {
  const [state, _setState] = React.useState<"none" | "cli" | "ui">("none");

  const [project, setProject] = React.useState("");
  const [serviceAccountName, setServiceAccountName] = React.useState("");
  const [saKeyJson, setSaKeyJson] = React.useState("");
  const [secretName, setSecretName] = React.useState(defaultSecretName);
  const [isCreating, setIsCreating] = React.useState(false);

  React.useEffect(() => {
    if (defaultSecretName && !secretName) {
      setSecretName(defaultSecretName);
    }
  }, [defaultSecretName]);

  async function handleCreateSecret() {
    if (!saKeyJson.trim()) {
      notification.warning({ message: "Please paste the Service Account Key JSON content" });
      return;
    }

    try {
      // Validate JSON
      JSON.parse(saKeyJson);
    } catch (e) {
      notification.error({ message: "Invalid JSON", description: "Please paste valid JSON content" });
      return;
    }

    const finalSecretName = secretName.trim() || defaultSecretName;
    if (!finalSecretName) {
      notification.warning({ message: "Please enter a secret name" });
      return;
    }

    try {
      setIsCreating(true);
      await request({
        method: "post",
        url: homeLink("secret"),
        body: {
          name: finalSecretName,
          kind: "secret",
          description: "GCP Service Account Key created inline for mk8s cluster",
          type: "gcp",
          data: saKeyJson,
        },
      });
      notification.success({ message: "Secret created successfully" });
      onSecretCreated?.(finalSecretName);
    } catch (e) {
      const errorMessage = e?.response?.data?.message || e.message;
      notification.error({ message: "Failed to create secret", description: errorMessage });
    } finally {
      setIsCreating(false);
    }
  }

  const projectCode = `<PROJECT>`;
  const serviceAccountNameCode = `<SA_NAME>`;

  function setState(from: "cli" | "ui") {
    if (state === from) {
      _setState("none");
    } else {
      _setState(from);
    }
  }

  const cliCommands = [
    `gcloud iam service-accounts create ${serviceAccountNameCode} \\
  --project=${projectCode} \\
  --display-name="${serviceAccountNameCode}"`,
    `gcloud projects add-iam-policy-binding ${projectCode} \\
  --member="serviceAccount:${serviceAccountNameCode}@${projectCode}.iam.gserviceaccount.com" \\
  --role="roles/compute.instanceAdmin.v1"`,
    `gcloud projects add-iam-policy-binding ${projectCode} \\
  --member="serviceAccount:${serviceAccountNameCode}@${projectCode}.iam.gserviceaccount.com" \\
  --role="roles/compute.loadBalancerAdmin"`,
    `gcloud projects add-iam-policy-binding ${projectCode} \\
  --member="serviceAccount:${serviceAccountNameCode}@${projectCode}.iam.gserviceaccount.com" \\
  --role="roles/compute.storageAdmin"`,
    `gcloud projects add-iam-policy-binding ${projectCode} \\
  --member="serviceAccount:${serviceAccountNameCode}@${projectCode}.iam.gserviceaccount.com" \\
  --role="roles/compute.securityAdmin"`,
    `gcloud projects add-iam-policy-binding ${projectCode} \\
  --member="serviceAccount:${serviceAccountNameCode}@${projectCode}.iam.gserviceaccount.com" \\
  --role="roles/compute.networkViewer"`,
    `gcloud iam service-accounts keys create ${serviceAccountNameCode}.json \\
  --iam-account=${serviceAccountNameCode}@${projectCode}.iam.gserviceaccount.com`,
  ]
    .map((cmd) =>
      cmd
        .replaceAll(projectCode, project || projectCode)
        .replaceAll(serviceAccountNameCode, serviceAccountName || serviceAccountNameCode),
    )
    .join(" && \\\n");

  return (
    <>
      <div className="mt-1 mb-2">
        <button type="button" className="block ngfocus color-link" onClick={() => setState("cli")}>
          How do I get my GCP Service Account Key with Google Cloud CLI?{" "}
          {state === "cli" ? (
            <ChevronUp style={{ transform: "translateY(2px)" }} className="feather-icon inline-block" />
          ) : (
            <ChevronDown style={{ transform: "translateY(2px)" }} className="feather-icon inline-block" />
          )}
        </button>
        {state === "cli" ? (
          <ul className="mt-1 mb-4 px-4 py-2 ml-4">
            <li className="list-decimal my-2">
              Install the{" "}
              <ExternalLink url="https://cloud.google.com/sdk/docs/install" label="Google Cloud CLI" disableCopy /> and{" "}
              <ExternalLink
                url="https://cloud.google.com/docs/authentication/gcloud"
                label="configure authentication"
                disableCopy
              />
              .
            </li>
            <li className="my-2 list-decimal">
              Enter your GCP <span className="font-semibold">Project ID</span> below.
            </li>
            <Input
              style={{ width: 450 }}
              placeholder="GCP Project ID"
              value={project}
              onChange={(e) => setProject(e.target.value)}
            />
            {project && (
              <>
                <li className="my-2 list-decimal">
                  Enter a name for the <span className="font-semibold">Service Account</span> (6-30 characters).
                </li>
                <Input
                  style={{ width: 450 }}
                  placeholder="Service Account Name (e.g., cpln-mk8s)"
                  value={serviceAccountName}
                  onChange={(e) => setServiceAccountName(e.target.value)}
                  status={
                    serviceAccountName && (serviceAccountName.length < 6 || serviceAccountName.length > 30)
                      ? "error"
                      : undefined
                  }
                />
                {serviceAccountName && (serviceAccountName.length < 6 || serviceAccountName.length > 30) && (
                  <div className="text-sm color-danger mt-1">
                    Service Account name must be between 6 and 30 characters (current: {serviceAccountName.length})
                  </div>
                )}
                {serviceAccountName && serviceAccountName.length >= 6 && serviceAccountName.length <= 30 && (
                  <>
                    <li className="my-2 list-decimal">
                      Execute the following commands to create the service account and grant the required roles.
                    </li>
                    <SyntaxHighlight code={cliCommands} language="bash" className="my-2" />
                    <li className="my-2 list-decimal">
                      The command above creates a file named{" "}
                      <span className="font-semibold">{serviceAccountName}.json</span>. Paste the contents of this file
                      below.
                    </li>
                    <div className="my-2">
                      <CodeEditor
                        value={saKeyJson}
                        setValue={setSaKeyJson}
                        language="json"
                        options={{
                          autoFocus: false,
                          basicSetup: {
                            lineNumbers: true,
                          },
                          style: {
                            maxHeight: 300,
                            overflow: "auto",
                            fontSize: 14,
                          },
                        }}
                        minHeight="150px"
                      />
                    </div>
                    {saKeyJson && (
                      <>
                        <li className="my-2 list-decimal">Optionally, enter a custom name for the secret.</li>
                        <Input
                          style={{ width: 450 }}
                          placeholder={defaultSecretName ? `Default: ${defaultSecretName}` : "Secret Name"}
                          value={secretName}
                          onChange={(e) => setSecretName(e.target.value)}
                        />
                        <div className="my-4">
                          <Button type="primary" onClick={handleCreateSecret} loading={isCreating}>
                            Create Secret{defaultSecretName && !secretName.trim() ? ` "${defaultSecretName}"` : ""}
                          </Button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </ul>
        ) : null}
        <button type="button" className="block ngfocus color-link" onClick={() => setState("ui")}>
          How do I get my GCP Service Account Key from GCP Console UI?{" "}
          {state === "ui" ? (
            <ChevronUp style={{ transform: "translateY(2px)" }} className="feather-icon inline-block" />
          ) : (
            <ChevronDown style={{ transform: "translateY(2px)" }} className="feather-icon inline-block" />
          )}
        </button>
        {state === "ui" ? (
          <ul className="mt-1 mb-4 px-4 py-2 ml-4">
            <li className="list-decimal my-2">
              Navigate to the{" "}
              <ExternalLink
                url="https://console.cloud.google.com/iam-admin/serviceaccounts"
                label="Service Accounts"
                disableCopy
              />{" "}
              page in GCP Console.
            </li>
            <li className="list-decimal my-2">
              Click <span className="font-semibold">Create Service Account</span>.
            </li>
            <li className="list-decimal my-2">
              Enter a name and ID for the service account, then click <span className="font-semibold">Create and Continue</span>.
            </li>
            <div className="flex items-center justify-center mb-2 border" style={{ width: 800 }}>
              <img
                className="object-container"
                style={{ width: 800 }}
                src={"/resources/howto/gcp_serviceaccount.png"}
                alt={"create a service account"}
              />
            </div>
            <li className="list-decimal my-2">
              Grant the following roles to the service account:
            </li>
            <ul className="ml-4 mb-2">
              <li className="list-disc">Compute Instance Admin (v1)</li>
              <li className="list-disc">Compute Load Balancer Admin</li>
              <li className="list-disc">Compute Storage Admin</li>
              <li className="list-disc">Compute Security Admin</li>
              <li className="list-disc">Compute Network Viewer</li>
            </ul>
            <div className="flex items-center justify-center mb-2 border" style={{ width: 800 }}>
              <img
                className="object-container"
                style={{ width: 800 }}
                src={"/resources/howto/gcp_access.png"}
                alt={"grant roles to service account"}
              />
            </div>
            <li className="list-decimal my-2">
              Click <span className="font-semibold">Done</span> to create the service account.
            </li>
            <li className="list-decimal my-2">
              Click on the newly created service account, go to the <span className="font-semibold">Keys</span> tab,
              and click <span className="font-semibold">Add Key → Create new key</span>.
            </li>
            <li className="list-decimal my-2">
              Select <span className="font-semibold">JSON</span> format and click{" "}
              <span className="font-semibold">Create</span>. A JSON file will be downloaded.
            </li>
            <li className="list-decimal my-2">
              Create a Secret of type <span className="font-semibold">GCP</span> with the contents of the downloaded
              JSON file.
            </li>
          </ul>
        ) : null}
      </div>
    </>
  );
};
